import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { StripeComplete } from "../components/StripeComplete";
import { useStripe } from "@stripe/react-stripe-js";
import { usePaymentContext } from "../contexts/payment";
import { usePurchaseContext } from "../contexts/purchase";

import { PurchasePageLayout } from "../components/PurchasePageLayout";

declare global {
  interface Window {
    dataLayer: object[];
  }
}

export function PurchaseComplete() {
  const { heyflowId } = useParams();
  const stripe = useStripe();

  const { purchaseInformation } = usePurchaseContext();
  const {
    fetchExistingPaymentIntent,
    paymentIntentResult,
    finalHeadline,
    paymentStatus,
    asyncPaymentMethodAndOrderId,
  } = usePaymentContext();

  // Invoice-Modus: Auftrag wurde ohne Stripe angelegt, kein payment_intent in URL.
  const isInvoice = new URLSearchParams(window.location.search).get("invoice") === "true";

  // Preview-Modus: ?_completePreview=true rendert die Success-Seite mit Mock-Daten,
  // ohne dass man den Checkout-Flow durchklicken muss. Nur für interne Vorschau.
  const isPreview = new URLSearchParams(window.location.search).get("_completePreview") === "true";
  const previewPurchaseInformation = isPreview
    ? ({
        animalName: "Schimmel",
        invoiceAddressFirstName: "Test",
        invoiceAddressLastName: "Pferdehalter",
        invoiceAddressStreet: "Teststr. 1",
        invoiceAddressZipCode: "30159",
        invoiceAddressCity: "Hannover",
        invoiceAddressCountry: "DE",
      } as any)
    : undefined;
  const previewLead = isPreview ? ({ offerId: "PREVIEW-001" } as any) : undefined;

  const asyncPaymentMethodAndOrderIdFetch = asyncPaymentMethodAndOrderId.fetch;
  useEffect(() => {
    if (!stripe || !heyflowId) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");

    if (!clientSecret) {
      return;
    }

    fetchExistingPaymentIntent(stripe, clientSecret);
    asyncPaymentMethodAndOrderIdFetch(heyflowId, clientSecret);
  }, [fetchExistingPaymentIntent, stripe, heyflowId, asyncPaymentMethodAndOrderIdFetch]);

  // originally I used the orderId here, but it could be slow to load
  // and then the purchase_completed event might not be sent before the user
  // closes the tab
  const orderId = heyflowId;
  const amount = paymentIntentResult?.paymentIntent?.amount;

  useEffect(() => {
    if (amount === undefined || !orderId) return;
    window.dataLayer?.push({ event: "purchase_completed", orderNumber: orderId, convValue: amount / 100 });
  }, [amount, orderId]);

  const refetchPendingInterval = useRef<number | undefined>(undefined);
  useEffect(() => {
    const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
    if (!clientSecret || !stripe) {
      return;
    }

    if (paymentStatus === "pending") {
      refetchPendingInterval.current = setInterval(() => {
        fetchExistingPaymentIntent(stripe, clientSecret);
      }, 5000);
    }

    return () => {
      if (refetchPendingInterval.current) {
        clearInterval(refetchPendingInterval.current);
        refetchPendingInterval.current = undefined;
      }
    };
  }, [fetchExistingPaymentIntent, paymentStatus, stripe]);

  return (
    <PurchasePageLayout
      isLoadingExtraData={
        isPreview
          ? false
          : isInvoice
          ? purchaseInformation === undefined
          : purchaseInformation === undefined || paymentIntentResult === undefined
      }
      loadingMessage="Deine Zahlung wird geladen"
      createHeadline={() => (isInvoice || isPreview ? "Vielen Dank für dein Vertrauen" : finalHeadline!)}
      showHeaderTickMark={isInvoice || isPreview || paymentStatus === "success"}
      dontShowPurchaseEditLink={true}
      dontShowNextStepsAndTestimonials={true}
      renderChild={({ lead, heyflowId }) => (
        <StripeComplete
          lead={isPreview ? previewLead : lead}
          heyflowId={heyflowId}
          purchaseInformation={(isPreview ? previewPurchaseInformation : purchaseInformation)!}
          paymentIntent={paymentIntentResult?.paymentIntent}
          paymentStatus={paymentStatus}
          paymentMethodPreview={asyncPaymentMethodAndOrderId.value?.paymentMethodPreview}
          paymentMethodPreviewIsLoading={asyncPaymentMethodAndOrderId.isLoading}
          isInvoiceMode={isInvoice || isPreview}
        />
      )}
    />
  );
}
